import { Button, FormControl, Grid, InputAdornment, Typography, Alert, Box } from "@mui/material";
import { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import { setTitle } from "store/reducer/appReducer";
import FormInput from "components/FormInput";
import PasswordStrengthIndicator from "components/PasswordStrengthIndicator";
import { useForm, SubmitHandler, FormProvider } from "react-hook-form";
import { object, string } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import Modal from "components/Dialog";
import Loading from "components/Loading";
import { authService } from "services/authentication/auth.service";
import { selectRootAdmin } from "store/reducer/appReducer";
import { IModal } from "interfaces/utils/utils.interface";
import { styles } from "assets/styles";
import { IconButton } from "@mui/material";
import { validatePasswordPolicy } from "helpers/passwordPolicy";
import api from "plugins/axios";

import { Visibility, VisibilityOff } from "@mui/icons-material";
import { defaultModal, saveFail, somethingWrong } from "helpers/utility";

type IChangePassword = {
  oldpassword: string;
  password: string;
  confirmPassword: string;
};

interface State {
  oldpassword: string;
  showOldPassword: boolean;
  password: string;
  showPassword: boolean;
  confirmPassword: string;
  showConfirmPassword: boolean;
}
export default function AdminChangePassword() {
  const isRootAdmin = useSelector(selectRootAdmin);
  const dispatch = useDispatch();
  const [values, setValues] = useState<State>({
    oldpassword: "",
    showOldPassword: false,

    password: "",
    showPassword: false,

    confirmPassword: "",
    showConfirmPassword: false,
  });
  const [modal, setModal] = useState<IModal>(defaultModal);
  const [loading, setLoading] = useState<boolean>(false);
  const [currentPassword, setCurrentPassword] = useState<string>("");
  const [passwordPolicyConfig, setPasswordPolicyConfig] = useState<{
    minPasswordLength: number;
    requireSpecialChar: boolean;
  }>({
    minPasswordLength: 12, // default fallback
    requireSpecialChar: true,
  });

  // Create dynamic schema based on password policy config
  const dynamicChangePasswordSchema = useMemo(() => object({
    oldpassword: string()
      .min(1, "กรุณาระบุรหัสผ่าน"),
    password: string()
      .min(1, "กรุณาระบุรหัสผ่าน")
      .refine((password) => {
        const result = validatePasswordPolicy(password, {
          minLength: passwordPolicyConfig.minPasswordLength,
          requireSpecialChar: passwordPolicyConfig.requireSpecialChar,
        });
        return result.isValid;
      }, " "), // Single space to prevent default "Invalid input" message
    confirmPassword: string()
      .min(1, "กรุณาระบุรหัสผ่าน"),
  }).refine((data) => data.password === data.confirmPassword, {
    message: "รหัสผ่านใหม่และยืนยันรหัสไม่ตรงกัน",
    path: ["confirmPassword"],
  }), [passwordPolicyConfig]);

  const defaultValues: IChangePassword = {
    oldpassword: "",
    password: "",
    confirmPassword: "",
  };
  const methods = useForm<IChangePassword>({
    resolver: zodResolver(dynamicChangePasswordSchema),
    defaultValues,
    mode: 'onChange', // Enable real-time validation
  });

  const { watch, trigger } = methods;
  const passwordValue = watch("password");

  // Update form resolver when password policy config changes
  useEffect(() => {
    methods.clearErrors(); // Clear existing errors
    // The resolver will be updated automatically by useMemo dependency
  }, [passwordPolicyConfig, methods]);

  // Re-trigger validation when password policy config changes
  useEffect(() => {
    if (passwordValue) {
      trigger('password'); // Re-validate password field with new config
    }
  }, [passwordPolicyConfig, trigger, passwordValue]);

  useEffect(() => {
    if (passwordValue) {
      setCurrentPassword(passwordValue);
    }
  }, [passwordValue]);
  useEffect(() => {
    dispatch(setTitle("เปลี่ยนรหัสผ่าน"));
    // Fetch password policy config
    const fetchPasswordPolicyConfig = async () => {
      try {
        const response = await api.get('/admin/password-policy-config');
        if (response.data && response.data.data) {
          setPasswordPolicyConfig({
            minPasswordLength: response.data.data.minPasswordLength || 12,
            requireSpecialChar: response.data.data.requireSpecialChar || true,
          });
        }
      } catch (error) {
        console.error('Failed to fetch password policy config:', error);
        // Use default values if fetch fails
      }
    };

    fetchPasswordPolicyConfig();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClickShowPasswordOld = () => {
    setValues({
      ...values,
      showOldPassword: !values.showOldPassword,
    });
  };
  const handleClickShowPassword = () => {
    setValues({
      ...values,
      showPassword: !values.showPassword,
    });
  };
  const handleClickShowPasswordCF = () => {
    setValues({
      ...values,
      showConfirmPassword: !values.showConfirmPassword,
    });
  };
  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };
  const onSubmitHandler: SubmitHandler<IChangePassword> = async (values: IChangePassword) => {
    setLoading(true);
    
    // Double-check password policy on submission
    const policyResult = validatePasswordPolicy(values.password, {
      minLength: passwordPolicyConfig.minPasswordLength,
      requireSpecialChar: passwordPolicyConfig.requireSpecialChar,
    });
    if (!policyResult.isValid) {
      setModal({
        open: true,
        message: `รหัสผ่านไม่ตรงตามข้อกำหนด: ${policyResult.errors.join(", ")}`,
        status: "error",
      });
      setLoading(false);
      return;
    }

    if (isRootAdmin) {
      await authService
        .updateRootPassword(values.oldpassword, values.password)
        .then(async (item: any) => {
          setModal({
            open: true,
            message: "เปลี่ยนรหัสผ่านสำเร็จ ระบบได้บันทึกข้อมูลการเปลี่ยนแปลงแล้ว",
            status: "success",
          });
          setLoading(false);
          methods.reset();
          setCurrentPassword("");
        })
        .catch((err) => {
          setLoading(false);
          if (err.response?.status === 400) {
            if (err.response?.data?.errors === "password is incorrect") {
              setModal({
                open: true,
                message: "รหัสผ่านเก่าไม่ถูกต้อง",
                status: "error",
              });
            } else if (err.response?.data?.errors?.includes("cannot be the same as current password")) {
              setModal({
                open: true,
                message: "รหัสผ่านใหม่ต้องไม่เหมือนกับรหัสผ่านปัจจุบัน",
                status: "error",
              });
            } else if (err.response?.data?.errors?.includes("password policy")) {
              setModal({
                open: true,
                message: "รหัสผ่านไม่ตรงตามข้อกำหนดความปลอดภัย",
                status: "error",
              });
            } else {
              setModal(saveFail);
            }
          } else {
            setModal(somethingWrong);
          }
        });
    } else {
      await authService
        .updateAdminPassword(values.oldpassword, values.password)
        .then(async () => {
          setModal({
            open: true,
            message: "เปลี่ยนรหัสผ่านสำเร็จ ระบบได้บันทึกข้อมูลการเปลี่ยนแปลงแล้ว",
            status: "success",
          });
          setLoading(false);
          methods.reset();
          setCurrentPassword("");
        })
        .catch((err) => {
          setLoading(false);
          if (err.response?.status === 400) {
            if (err.response?.data?.errors === "password is incorrect") {
              setModal({
                open: true,
                message: "รหัสผ่านเก่าไม่ถูกต้อง",
                status: "error",
              });
            } else if (err.response?.data?.errors?.includes("cannot be the same as current password")) {
              setModal({
                open: true,
                message: "รหัสผ่านใหม่ต้องไม่เหมือนกับรหัสผ่านปัจจุบัน",
                status: "error",
              });
            } else if (err.response?.data?.errors?.includes("password policy")) {
              setModal({
                open: true,
                message: "รหัสผ่านไม่ตรงตามข้อกำหนดความปลอดภัย",
                status: "error",
              });
            } else {
              setModal(saveFail);
            }
          } else {
            setModal(somethingWrong);
          }
        });
    }
  };
  return (
    <>
      <Loading loading={loading} />
      <Modal
        props={{
          modalOpen: modal.open,
          modalStatus: modal.status,
          modalMessage: modal.message,
          handleModal: setModal,
        }}
      />

      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmitHandler)}>
          <Grid container pl={5} alignItems="start" pt={5}>
            <Grid item xs={12} sm={12} md={12} lg={3} xl={2} container alignItems="center" mr={4}>
              <Typography mt={1} className="label-input">
                <span className="req">*</span>
                รหัสผ่านเก่า
              </Typography>
            </Grid>
            <Grid item xs={12} sm={12} md={12} lg={8} xl={8}>
              <FormControl fullWidth variant="outlined">
                <FormInput
                  sx={{ mb: "1.5rem" }}
                  fullWidth
                  size="small"
                  name="oldpassword"
                  placeholder="ระบุรหัสผ่านเก่า"
                  type={values.showOldPassword ? "text" : "password"}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={handleClickShowPasswordOld}
                          onMouseDown={handleMouseDownPassword}
                          edge="end"
                        >
                          {values.showOldPassword ? <Visibility /> : <VisibilityOff />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </FormControl>
            </Grid>
          </Grid>
          <Grid container pl={5} alignItems="start">
            <Grid item xs={12} sm={12} md={12} lg={3} xl={2} container alignItems="center" mr={4}>
              <Typography mt={1} className="label-input">
                <span className="req">*</span>
                รหัสผ่านใหม่
              </Typography>
            </Grid>
            <Grid item xs={12} sm={12} md={12} lg={8} xl={8}>
              <FormControl fullWidth variant="outlined">
                <FormInput
                  sx={{ mb: "1rem" }}
                  fullWidth
                  size="small"
                  name="password"
                  placeholder="ระบุรหัสผ่านใหม่"
                  type={values.showPassword ? "text" : "password"}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton aria-label="toggle password visibility" onClick={handleClickShowPassword} onMouseDown={handleMouseDownPassword} edge="end">
                          {values.showPassword ? <Visibility /> : <VisibilityOff />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                {currentPassword && (
                  <Box sx={{ mb: 1 }}>
                    <PasswordStrengthIndicator
                      password={currentPassword}
                      showRequirements={true}
                      showStrengthBar={true}
                      compact={false}
                      minPasswordLength={passwordPolicyConfig.minPasswordLength}
                      requireSpecialChar={passwordPolicyConfig.requireSpecialChar}
                    />
                  </Box>
                )}
                <Alert severity="info" sx={{ mb: 1 }}>
                  <Typography variant="body2">
                    <strong>ข้อกำหนดรหัสผ่านใหม่:</strong>
                    <br />
                    • รหัสผ่านต้องมีอย่างน้อย {passwordPolicyConfig.minPasswordLength} ตัวอักษร
                    <br />
                    {passwordPolicyConfig.requireSpecialChar && (
                      <>• ต้องมีอักขระพิเศษอย่างน้อย 1 ตัว<br /></>
                    )}
                  </Typography>
                </Alert>
              </FormControl>
            </Grid>
          </Grid>
          <Grid container pl={5} alignItems="start">
            <Grid item xs={12} sm={12} md={12} lg={3} xl={2} container alignItems="center" mr={4}>
              <Typography mt={1} className="label-input">
                <span className="req">*</span>
                ยืนยันรหัสผ่านใหม่
              </Typography>
            </Grid>
            <Grid item xs={12} sm={12} md={12} lg={8} xl={8}>
              <FormControl fullWidth variant="outlined">
                <FormInput
                  sx={{ mb: "1.5rem" }}
                  fullWidth
                  size="small"
                  name="confirmPassword"
                  placeholder="ระบุยืนยันรหัสผ่านใหม่"
                  type={values.showConfirmPassword ? "text" : "password"}
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={handleClickShowPasswordCF}
                          onMouseDown={handleMouseDownPassword}
                          edge="end"
                        >
                          {values.showConfirmPassword ? <Visibility /> : <VisibilityOff />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </FormControl>
            </Grid>
          </Grid>
          <Grid container justifyContent="center" alignItems="start">
            <Grid container my={2} item xs={2}>
              <Button sx={styles.btnConfirm} variant="contained" color="primary" type="submit">
                บันทึก
              </Button>
            </Grid>
          </Grid>
        </form>
      </FormProvider>
    </>
  );
}
